import React, { useState, memo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { ShadowOverlay } from 'pcln-design-system'
import Calendar, { CalendarPropTypes, CalendarDefaultProps } from '../Calendar'
import DateField from '../DateField'
import formatDates from '../utils/formatDates'

const StyledCalendar = styled(Calendar)`
  position: absolute;
  z-index: ${(props) => props.zIndex};
`

function DatePicker({
  className,
  isInitiallyOpen,
  initialDates,
  label,
  width,
  zIndex,
  onChange,
  onFocus,
  onBlur,
  errorMessage,
  ...calendarProps
}) {
  const [isOpen, setOpen] = useState(isInitiallyOpen)
  const [dates, setDates] = useState(initialDates)

  const dateString = formatDates(calendarProps.selectTwoDates, dates)

  const handleOpen = () => {
    setOpen(true)
    onFocus && onFocus()
  }

  const handleClose = () => {
    setOpen(false)
    onBlur && onBlur()
    onChange && onChange(dates)
  }

  const handleChange = (evt, dates) => {
    setDates(dates)
    if (dates.endDate !== null) {
      setOpen(false)
      onBlur && onBlur()
      onChange && onChange(dates)
    }
  }

  return (
    <>
      {isOpen && <ShadowOverlay zIndex={zIndex} onClick={handleClose} />}
      <DateField
        className={className}
        isOpen={isOpen}
        label={label}
        value={dateString}
        width={width}
        zIndex={isOpen ? zIndex : 0}
        onFocus={handleOpen}
        bg='background.lightest'
        mb={2}
        error={errorMessage}
      />
      {isOpen && (
        <StyledCalendar
          zIndex={zIndex + 1}
          startDate={dates.startDate}
          endDate={dates.endDate}
          onChange={handleChange}
          onDismiss={handleClose}
          {...calendarProps}
        />
      )}
    </>
  )
}

DatePicker.displayName = 'DatePicker'

DatePicker.propTypes = {
  className: PropTypes.string,
  isInitiallyOpen: PropTypes.bool,
  initialDates: PropTypes.object,
  label: PropTypes.string,
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]),
  zIndex: PropTypes.number,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  errorMessage: PropTypes.string,
  ...CalendarPropTypes,
}

DatePicker.defaultProps = {
  className: '',
  initialDates: { startDate: null, endDate: null },
  label: 'Check-in - Check-out',
  zIndex: 2,
  ...CalendarDefaultProps,
}

export default memo(DatePicker)
